import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Footer from "../Footer";
import ImportTable from "./ImportTable";
import ImportModal from "../../modals/ImportModal";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import UserContext from "../../utils/Contexts/UserContext";
import _ from "lodash";
import TablePagination from "../Common/Pagination";
import { useLoading } from "../../utils/Contexts/LoadingContext";
import ThemeContext from "../../utils/Contexts/ThemeContext";

import "./style.css"

function Imports() {
  const { t } = useContext(LanguageContext);
  const { selectedTheme } = useContext(ThemeContext);
  const { userData } = useContext(UserContext);
  const { setIsLoading } = useLoading();
  const [importData, setImportData] = useState(null);
  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [columns, setColumns] = useState({
    file: true,
    date: true,
    product: true,
    type: true,
    status: true,
    items: true,
    imports: true,
    details: true,
  });
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      { data: "file_name" },
      { data: "created" },
      { data: "product" },
      { data: "type" },
      { data: "status" },
      { data: "uploaded" },
    ],
    order: [{ column: 1, dir: "desc" }],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [],
    },
  });
  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: "desc",
    2: null,
    3: null,
    4: null,
    5: null,
    6: null,
  });

  const fetchImportsData = () => {
    const payload = _.cloneDeep(formData);

    const url = `/users/${userData?.uuid}/imports/search`;
    setIsLoading(true);
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setImportData(data);
        setRecordsFiltered(filtered);
        setIsLoading(false);
      })
      .catch(() => {
        toast.error(t("There was an error fetching imports data"));
      });
  };

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      fetchImportsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  // sort
  useEffect(() => {
    const order = Object.keys(columnSorts)
      .filter((columnIndex) => columnSorts[columnIndex])
      .map((columnIndex) => ({
        column: Number(columnIndex),
        dir: columnSorts[columnIndex],
      }));

    if (!_.isEqual(formData.order, order)) {
      setFormData((prev) => ({
        ...prev,
        order,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  // page
  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      search: {
        ...prevData.search,
        value: searchTerm,
      },
    }));
  }, [searchTerm]);

  // eslint-disable-next-line
  const debouncedSearch = useCallback(
    _.debounce((searchTerm) => {
      setActivePage(1);
      setSearchTerm(searchTerm);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSearch(search);
    return () => {
      debouncedSearch.cancel();
    };
  }, [search, debouncedSearch]);

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div
          className="page-header pb-0 mb-4"
          style={{ borderBottom: "unset" }}
        >
          <div className="row align-items-center mb-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  {/* eslint-disable-next-line */}
                  <a className="breadcrumb-link" href="javascript:;">
                    {t("Websites")}
                  </a>
                </li>
              </ol>
            </nav>

            <div className="col-md mb-2 mb-md-0">
              <h1 className="page-header-title">{t("Your Imports")}</h1>
            </div>

            <div className="col-sm-auto">
              <button
                className="btn btn-primary button-import p-0"
                data-bs-toggle="modal"
                data-bs-target="#importModal"
              >
                <i className="bi-download"></i> {t("Import")}
              </button>
            </div>
          </div>
        </div>
        <div
          className="mb-4"
          style={{
            borderBottom: `.0625rem solid ${
              selectedTheme === "dark" ? "#2f3235" : "rgba(231,234,243,.7)"
            }`,
            marginLeft: "-30px",
            marginRight: "-30px",
          }}
        ></div>
        <div className="card">
          <div className="card-header d-flex justify-content-between flex-row gap-2">
            <div className="search-input-container">
              <div className="input-group input-group-merge input-group-flush border rounded-2 pe-2">
                <div className="input-group-prepend input-group-text px-2">
                  <i className="bi-search"></i>
                </div>
                <input
                  id="datatableSearch"
                  type="search"
                  className="form-control custom-placeholder"
                  placeholder={t("Search Imports")}
                  aria-label="Search Imports"
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="d-grid d-flex align-items-center gap-2">
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-white w-100 text-nowrap text-secondary"
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                >
                  <i className="bi bi-layout-three-columns me-1"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ImportTable
            importData={importData}
            setImportData={setImportData}
            columnsVisibility={columns}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
          />
          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>
      <ImportModal fetchImportsData={fetchImportsData} flag={false} />
      <Footer />
    </main>
  );
}

export default Imports;
